const MAX_FILE_SIZE = 1024 * 1024; // 1MB in bytes
const MAX_WIDTH = 1024;
const MAX_HEIGHT = 1024;
const INITIAL_QUALITY = 0.7;
const MIN_QUALITY = 0.1;

export async function compressImage(file: File): Promise<File> {
    // If file is already less than 1MB, return it as is
    if (file.size <= MAX_FILE_SIZE) {
        return file;
    }

    // Create a data URL from the file
    const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
    });

    // Load the image
    const img = await new Promise<HTMLImageElement>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = dataUrl;
    });

    // Calculate dimensions while maintaining aspect ratio
    let width = img.width;
    let height = img.height;

    if (width > MAX_WIDTH) {
        height = Math.round((height * MAX_WIDTH) / width);
        width = MAX_WIDTH;
    }

    if (height > MAX_HEIGHT) {
        width = Math.round((width * MAX_HEIGHT) / height);
        height = MAX_HEIGHT;
    }

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    // Draw image on canvas
    ctx.drawImage(img, 0, 0, width, height);

    // Compress with decreasing quality until file size is under 1MB
    let quality = INITIAL_QUALITY;
    let blob: Blob | null = null;
    
    while (quality >= MIN_QUALITY) {
        blob = await new Promise<Blob | null>(resolve => {
            canvas.toBlob(
                resolve,
                'image/jpeg',
                quality
            );
        });

        if (blob && blob.size <= MAX_FILE_SIZE) {
            break;
        }

        quality -= 0.1;
    }

    // If we couldn't compress enough with quality reduction,
    // we'll need to reduce dimensions further
    if (!blob || blob.size > MAX_FILE_SIZE) {
        const scale = Math.sqrt(MAX_FILE_SIZE / blob!.size);
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        blob = await new Promise<Blob | null>(resolve => {
            canvas.toBlob(
                resolve,
                'image/jpeg',
                MIN_QUALITY
            );
        });
    }

    if (!blob) {
        throw new Error('Failed to compress image');
    }

    // Create new file from blob
    return new File([blob], file.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
    });
}

// Helper function to format file size
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
} 